import React, { type ReactElement, type ReactNode, useEffect, useState } from 'react';

import * as Sentry from '@sentry/nextjs';

import { defaultLanguage as DEFAULT_LANGUAGE } from '@i18n';

import useTranslation from '@framework/hooks/useTranslation';
import { I18N_NEXT_COOKIE } from '@framework/utils/cookies';

import { Loading } from 'styled-system';

import styles from './TranslationReadyGuard.module.scss';

type TranslationReadyGuardProps = {
    children: ReactNode;
    fallback?: ReactNode;
};

/**
 * Retrieves the user's language preference from the cookie.
 * Doesn't use react-cookie to avoid hydration issues.
 * @returns {string} The language code from the cookie or the default language.
 */
const getLanguageFromCookie = (): string => {
    if (typeof document === 'undefined') {
        return DEFAULT_LANGUAGE;
    }

    try {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === I18N_NEXT_COOKIE && value) {
                return decodeURIComponent(value);
            }
        }
    } catch {
        Sentry.captureMessage('Error reading language cookie');
    }
    return DEFAULT_LANGUAGE;
};

const DefaultFallback = (): ReactElement => (
    <div className={styles.loaderContainer}>
        <Loading size={48} />
    </div>
);

const TranslationReadyGuard = ({
    children,
    fallback = <DefaultFallback />
}: TranslationReadyGuardProps): ReactElement => {
    const { ready, i18n } = useTranslation();
    const [isLanguageCorrect, setIsLanguageCorrect] = useState(false);

    const { changeLanguage, language } = i18n;

    useEffect(() => {
        const userLanguage = getLanguageFromCookie();
        const currentLanguage = language;

        if (currentLanguage === userLanguage) {
            if (ready) {
                setIsLanguageCorrect(true);
            }
            return;
        }

        setIsLanguageCorrect(false);

        changeLanguage(userLanguage).then(() => {
            setIsLanguageCorrect(true);
        });
    }, [changeLanguage, language, ready]);

    if (!isLanguageCorrect || !ready) {
        return fallback as ReactElement;
    }

    return children as ReactElement;
};

export default TranslationReadyGuard;
